// =============================================================================
// FILE: packages/renderer/src/stores/elite.store.ts
// PURPOSE: "Identity Transformation" state. Tracks whether the operator has
//          verified an RCDD credential — which permanently unlocks the Gold
//          Enterprise Edition experience for the duration of their 100-day
//          trial. Persisted to localStorage so the elite state survives reloads.
// =============================================================================

import { create } from 'zustand';

const LS_KEY = 'rcd2.elite.v1';
const TRIAL_DAYS = 100;

interface Persisted { bicsiId: string; verifiedAtIso: string; }

function load(): Persisted | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<Persisted>;
    if (!p || typeof p.bicsiId !== 'string' || typeof p.verifiedAtIso !== 'string') return null;
    return { bicsiId: p.bicsiId, verifiedAtIso: p.verifiedAtIso };
  } catch { return null; }
}

/** BICSI RCDD IDs are numeric (6–8 digits). Accept that as a verifiable format. */
export function isValidRcddId(raw: string): boolean {
  return /^\d{6,8}$/.test(raw.trim());
}

function daysLeft(iso: string): number {
  const elapsed = (Date.now() - new Date(iso).getTime()) / 86_400_000;
  return Math.max(0, Math.ceil(TRIAL_DAYS - elapsed));
}

interface EliteState {
  verified: boolean;
  bicsiId: string | null;
  daysRemaining: number;
  verify: (bicsiId: string) => void;
  reset: () => void;
}

const initial = load();
const initValid = initial ? daysLeft(initial.verifiedAtIso) > 0 : false;

export const useEliteStore = create<EliteState>((set) => ({
  verified: initValid,
  bicsiId: initValid ? initial!.bicsiId : null,
  daysRemaining: initValid ? daysLeft(initial!.verifiedAtIso) : 0,

  verify: (bicsiId) => {
    const rec: Persisted = { bicsiId: bicsiId.trim(), verifiedAtIso: new Date().toISOString() };
    try { localStorage.setItem(LS_KEY, JSON.stringify(rec)); } catch { /* */ }
    set({ verified: true, bicsiId: rec.bicsiId, daysRemaining: TRIAL_DAYS });
  },

  reset: () => {
    try { localStorage.removeItem(LS_KEY); } catch { /* */ }
    set({ verified: false, bicsiId: null, daysRemaining: 0 });
  },
}));
